type CsrfResponse = {
  csrf_token: string;
};

export type SavedPlaceItem = {
  venue: {
    id: string;
    name: string | null;
    category: string | null;
    city: { id: string; name: string };
    cover_url: string | null;
    hopper_score: number | null;
  };
  note: string | null;
  saved_at: string | null;
  friends_who_saved_count: number;
};

export type SavedPlacesPage = {
  items: SavedPlaceItem[];
  pagination: {
    limit: number;
    has_more: boolean;
    next_cursor: string | null;
  };
};

async function csrfToken(): Promise<string> {
  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`CSRF failed: ${response.status}`);
  }

  const body = (await response.json()) as CsrfResponse;

  if (!body.csrf_token) {
    throw new Error('CSRF token is missing');
  }

  return body.csrf_token;
}

export async function fetchSavedPlacesPage(cursor: string | null = null, limit = 50): Promise<SavedPlacesPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);

  const response = await fetch(`/api/v1/me/saved-places?${params.toString()}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Saved places request failed: ${response.status}`);
  }

  return response.json() as Promise<SavedPlacesPage>;
}

export async function fetchSavedVenueIds(): Promise<Set<string>> {
  const ids = new Set<string>();
  let cursor: string | null = null;

  do {
    const page = await fetchSavedPlacesPage(cursor, 50);
    page.items.forEach((item) => ids.add(item.venue.id));
    cursor = page.pagination.has_more ? page.pagination.next_cursor : null;
  } while (cursor);

  return ids;
}

export async function saveVenue(venueId: string): Promise<void> {
  const csrf = await csrfToken();
  const response = await fetch('/api/v1/me/saved-places', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrf,
    },
    body: JSON.stringify({ venue_id: venueId }),
  });

  if (!response.ok) {
    throw new Error(`Save venue failed: ${response.status}`);
  }
}

export async function unsaveVenue(venueId: string): Promise<void> {
  const csrf = await csrfToken();
  const response = await fetch(`/api/v1/me/saved-places/${encodeURIComponent(venueId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrf,
    },
  });

  if (!response.ok) {
    throw new Error(`Unsave venue failed: ${response.status}`);
  }
}
